import { IRowData } from "@multiweb/types/contracts";
import { InvalidArgumentException } from "../../../Global/Base/Exceptions";
import { CultureInfo } from "../../../Global/Dom/CultureInfo";
import { AttrValueReader } from "../../../OW.Remoting/Remoting/AttrValueReader";
import { GenericControl } from "../../UI.BaseControls/GenericControl";
import { registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarGrid } from "../CalendarGrid";
import { CalendarItem } from "../DataGridSubcontrols/Columns/CalendarItem";
import { CalendarDataRow } from "../DataGridSubcontrols/Components/CalendarDataRow";
import { AggregatedDataSource } from "../DataSource/AggregatedDataSource";
import { EditableDBDataSource } from "../DataSource/EditableDBDataSource";
import { CalendarEventItem, CalendarEventItemType } from "./CalendarEventItem";
/**
     * Ukázkový kód pro založení prázdného elementu
     */
export class CalendarEvent extends CalendarDataRow {
    /**
     * Unikatní ID pro Agregovany DataSource
     */
    public uniqeDSId: string;

    protected _calItems: Map<string, CalendarEventItem> = new Map<string, CalendarEventItem>();

    private _rowNum: number = 0;

    private _canShowTooltipFunction: () => {} | undefined;

    //#region Properties
    public override set visible(v: boolean) {
        if (this.checkAndLogDisposedUsage("set visible")) return;

        this.classList.toggle("Hidden", !v);
    }

    public override get visible(): boolean {
        if (this.checkAndLogDisposedUsage("get visible")) return false;

        return !this.classList.contains("Hidden");
    }

    public override set enabled(v: boolean) {
        if (this.checkAndLogDisposedUsage("set enabled")) return;

        this.classList.toggle("disabled", !v);
    }

    public override get enabled(): boolean {
        if (this.checkAndLogDisposedUsage("get enabled")) return false;

        return !this.classList.contains("disabled");
    }

    public override get isAttached(): boolean {
        if (this.checkAndLogDisposedUsage("get isAttached")) return false;

        return this._isAttached;
    }

    public override get dataGrid(): CalendarGrid {
        if (this.checkAndLogDisposedUsage("get dataGrid")) {
            return this._dataGrid;
        }

        return this._dataGrid;
    }

    //public set dataKey(v: string) { this._dataKey = v; }

    /**
     * Délka události v sekundách
     */
    public get eventDuration(): number {
        if (this.checkAndLogDisposedUsage("get eventDuration")) return -1;

        return this.dateTo.getTime() - this.dateFrom.getTime();
    }

    /**
     * Řádek na kterém se nachazí událost (pomocná proměnná pro spravne napozicovani vícedenních události)
     */
    public get rowNum(): number {
        if (this.checkAndLogDisposedUsage("get rowNum")) return -1;

        return this._rowNum;
    }

    public set rowNum(v: number) {
        if (this.checkAndLogDisposedUsage("set rowNum")) return;

        if (this._rowNum < v)
            this._rowNum = v;
    }

    public get canShowTooltipFunction(): () => {} {
        if (this.checkAndLogDisposedUsage("get canShowTooltipFunction")) return () => ({});

        return this._canShowTooltipFunction;
    }
    //#endregion Properties

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected override _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
    }

    public static createEventByIRowData(ownerGrid: CalendarGrid, uniqeDSId: string, data: IRowData): CalendarEvent {
        const ce = this.createEvent(ownerGrid, uniqeDSId, data, CalendarEventType.range);
        return ce;
    }

    protected static _prepareEvent<T extends CalendarEvent>(calendarEvent: T, ownerGrid: CalendarGrid, uniqeDSId: string, data: IRowData): T {
        calendarEvent._dataGrid = ownerGrid;
        calendarEvent.uniqeDSId = uniqeDSId;

        calendarEvent._canShowTooltipFunction = () => {
            return ownerGrid.isActive;
        };

        const dataKey = (ownerGrid.dataSource as AggregatedDataSource<EditableDBDataSource>).getDataKey(uniqeDSId);
        calendarEvent._setDataKey(uniqeDSId, AttrValueReader.read(data[dataKey]));
        calendarEvent._caption = AttrValueReader.read(data["caption"]);
        calendarEvent._dateFrom = AttrValueReader.read(data["datefromfld"]);
        calendarEvent._dateTo = AttrValueReader.read(data["datetofld"]);

        const ownerColumn = (ownerGrid.columns as CalendarItem[]).find(item => item.itemId == uniqeDSId);
        if (ownerColumn) {
            calendarEvent._includeTime = ownerColumn.includeTime;
            calendarEvent._color = ownerColumn.color;
            calendarEvent._showFormID = ownerColumn.showFormID;
            calendarEvent._objectName = ownerColumn.objectName;
            calendarEvent._condition = ownerColumn.condition;
            calendarEvent._textFields = ownerColumn.textFields;
            if (calendarEvent.textFields)
                calendarEvent.textFields.forEach(f => calendarEvent._textFieldsValues.push(AttrValueReader.read(data[f])));
        }

        if (calendarEvent.dateFrom > calendarEvent.dateTo)
            throw new InvalidArgumentException("Start date can not greater than end date");

        return calendarEvent;
    }

    protected static _createEventItem(ce: CalendarEvent, sDate: Date, lastCEI: CalendarEventItem, type: CalendarEventType, curCultName: string, timeOptions: Intl.DateTimeFormatOptions, id: string): CalendarEventItem {
        const cei = new CalendarEventItem();

        cei.type = lastCEI == null ? CalendarEventItemType.start : CalendarEventItemType.continuously;

        ce._initOneItem(cei);

        cei.currentDate = new Date(sDate);

        if (cei.type == CalendarEventItemType.start || sDate.getDay() == ce.dataGrid.firstDayOfWeek) {
            cei.caption = ce.caption;
            if (type != CalendarEventType.allDay)
                cei.timeText = ce.dateFrom.toLocaleTimeString(curCultName, timeOptions);
        }
        ce._calItems.set(id, cei);

        cei.mouseOver.add((_sender, prm) => {
            ce._calItems.forEach((aktItem) => aktItem.mouseOverStyle = prm);
        });
        return cei;
    }

    public static createEvent(ownerGrid: CalendarGrid, uniqeDSId: string, data: IRowData, type: CalendarEventType): CalendarEvent {
        const calendarEvent = CalendarEvent._prepareEvent(new CalendarEvent(), ownerGrid, uniqeDSId, data);

        const curCult = CultureInfo.getCultureInfo();
        const curCultName = curCult.name;
        const timeOptions: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

        const sDate = new Date(calendarEvent.dateFrom);
        const eDate = new Date(calendarEvent.dateTo);

        sDate.setHours(12, 0, 0, 0);
        eDate.setHours(12, 0, 0, 0);

        if (CalendarEvent._compareDate(calendarEvent.dateFrom, calendarEvent.dateTo)) {
            const cei = new CalendarEventItem();
            cei.type = CalendarEventItemType.standalone;
            cei.currentDate = new Date(sDate);

            calendarEvent._initOneItem(cei);

            cei.caption = calendarEvent.caption;
            if (type != CalendarEventType.allDay)
                cei.timeText = calendarEvent.dateFrom.toLocaleTimeString(curCultName, timeOptions);

            cei.mouseOver.add((_sender, prm) => {
                calendarEvent._calItems.forEach((aktItem) => aktItem.mouseOverStyle = prm);
            });
            calendarEvent._calItems.set(`${sDate.getDate()}|${sDate.getMonth()}|${sDate.getFullYear()}`, cei); // `
        } else {
            let lastCEI: CalendarEventItem = null;
            while (sDate <= eDate) {
                const id = `${sDate.getDate()}|${sDate.getMonth()}|${sDate.getFullYear()}`;
                lastCEI = CalendarEvent._createEventItem(calendarEvent, sDate, lastCEI, type, curCultName, timeOptions, id); // `

                //   lastCEI.mouseOver.add((sender, prm) => {
                //       calendarEvent._calItems.forEach((aktItem) => aktItem.mouseOverStyle = prm);
                //   });

                sDate.setDate(sDate.getDate() + 1);
            }

            if (lastCEI)
                lastCEI.type = CalendarEventItemType.end;
        }

        return calendarEvent;
    }

    protected _initOneItem(cei: CalendarEventItem): void {
        if (this.checkAndLogDisposedUsage("_initOneItem")) return;

        cei.parentEvent = this;
        if (this.color)
            cei.classList.add("flag", this.color);
    }

    public getCalendarEventItem(date: Date): CalendarEventItem {
        if (this.checkAndLogDisposedUsage("getCalendarEventItem")) return undefined;

        const id = `${date.getDate()}|${date.getMonth()}|${date.getFullYear()}`;

        return this._calItems.get(id);
    }

    public getCalendarEventItems(): IterableIterator<CalendarEventItem> {
        if (this.checkAndLogDisposedUsage("getCalendarEventItems")) return undefined;

        return this._calItems.values();
    }

    private static _compareDate(d1: Date, d2: Date): boolean {
        return d1.getDate() == d2.getDate() && d1.getMonth() == d2.getMonth() && d1.getFullYear() == d2.getFullYear();
    }

    // ReSharper disable once InconsistentNaming
    protected override connectedCallback(): void {
        if (this.checkAndLogDisposedUsage("connectedCallback")) return;

        this._isAttached = true;
        GenericControl.callEvent(this, "attached");
    }

    // ReSharper disable once InconsistentNaming
    protected override disconnectedCallback(): void {
        if (this.checkAndLogDisposedUsage("disconnectedCallback")) return;

        this._isAttached = false;
        GenericControl.callEvent(this, "detached");
        this.unregisterEventListener();
    }

    // ReSharper disable once InconsistentNaming
    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        if (this.checkAndLogDisposedUsage("attributeChangedCallback")) return;

        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public override registerEventListener(event: string, action: (e: Event) => void): void {
        if (this.checkAndLogDisposedUsage("registerEventListener")) return;

        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public override unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        if (this.checkAndLogDisposedUsage("unregisterEventListener")) return;

        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public override callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        if (this.checkAndLogDisposedUsage("callEvent")) return undefined;

        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public override offset(): { top: number; left: number; width: number; height: number } {
        if (this.checkAndLogDisposedUsage("offset")) return undefined;

        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

export enum CalendarEventType {
    allDay,
    range,
    exactDate,
}

registerCustomElement("tsc-calendargridCalendarEvent", CalendarEvent);
